import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Check, Building2, Handshake, MapPin } from "lucide-react";
import { brand } from "../mock/content";
import { track } from "../lib/analytics";
import { t } from "../lib/ui";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPES = ["Residenziale", "Commerciale & Industriale", "Utility Scale"];
const ACTIVITIES = ["Sopralluogo", "Progettazione", "Installazione elettrica", "Installazione meccanica", "Pratiche", "Collaudo", "Manutenzione"];
const TEAMS = ["1", "2", "3", "4+"];
const POWERS = ["<100 kWp", "100–500 kWp", "500 kW–1 MW", ">1 MW"];

const emptyPartner = {
  installsDirectly: "",
  types: [],
  activities: [],
  internalTeams: "",
  numTeams: "",
  annualPower: "",
  electricalCert: "",
  availableTurnkey: "",
  availableMaintenance: "",
  operatingArea: "",
};

export function ContactForm({
  title,
  subtitle,
  defaultTipo = "Privato",
  source = "Contatti",
  productName,
}) {
  const [form, setForm] = useState({ nome: "", azienda: "", email: "", telefono: "", tipo: defaultTipo, messaggio: "", companyType: "", indirizzo: "" });
  const [partner, setPartner] = useState(emptyPartner);
  const [hp, setHp] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setP = (k, v) => setPartner((p) => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setPartner((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const isPartner = form.tipo === "Installatore Partner";
  const isCompany = form.tipo === "Azienda";
  const isPrivato = form.tipo === "Privato";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nome || !form.email || !form.telefono) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        hp,
        source: productName ? `${source} · ${productName}` : source,
        productName: productName || null,
        partner: isPartner ? partner : null,
        companyType: isCompany ? form.companyType : null,
      };
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("bad");
      setSent(true);
      track("generate_lead", {
        form_type: form.tipo,
        source: productName ? `${source} · ${productName}` : source,
        product_name: productName || undefined,
      });
      setForm({ nome: "", azienda: "", email: "", telefono: "", tipo: defaultTipo, messaggio: "", companyType: "", indirizzo: "" });
      setPartner(emptyPartner);
      setTimeout(() => setSent(false), 6000);
    } catch {
      setError(t("cf_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#f4f4f5] border border-black/10 p-8 md:p-10" data-testid="contact-form">
      {/* honeypot anti-spam — hidden from users */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
      <h2 className="font-black text-2xl tracking-tight text-black" style={{ fontFamily: "Archivo" }}>{title || t("cf_title")}</h2>
      {subtitle && <p className="mt-2 text-black/50 text-sm" style={{ fontFamily: "Inter" }}>{subtitle}</p>}

      {/* Tipo */}
      <div className="flex flex-wrap gap-3 mt-8 mb-8">
        {[["Privato", "cf_type_privato"], ["Azienda", "cf_type_azienda"], ["Installatore Partner", "cf_type_partner"]].map(([val, key]) => (
          <button
            key={val}
            type="button"
            data-testid={`tipo-tab-${val.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setForm((f) => ({ ...f, tipo: val }))}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border transition-colors ${form.tipo === val ? "bg-black text-white border-black" : "border-black/20 text-black/70 hover:border-black"}`}
            style={{ fontFamily: "Space Grotesk" }}
          >
            {val === "Azienda" && <Building2 size={15} />} {t(key)}
          </button>
        ))}
      </div>

      {isPartner && (
        <div data-testid="partner-intro" className="mb-8 border-l-2 border-[#F9E212] bg-black text-white p-6">
          <div className="flex items-center gap-2 mb-3">
            <Handshake size={18} className="text-[#F9E212]" />
            <span className="text-xs uppercase tracking-[0.14em] text-[#F9E212]" style={{ fontFamily: "Space Grotesk" }}>{t("cf_badge")}</span>
          </div>
          <h3 className="font-black text-lg mb-2" style={{ fontFamily: "Archivo" }}>{t("cf_partner_h")}</h3>
          <p className="text-sm text-white/70 leading-relaxed" style={{ fontFamily: "Inter" }}>
            {t("cf_partner_p")}
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <Field label={t("cf_nome")} value={form.nome} onChange={update("nome")} required testid="field-nome" />
        {!isPrivato && (
          <Field label={(isPartner || isCompany) ? t("cf_azienda_req") : t("cf_azienda")} value={form.azienda} onChange={update("azienda")} required={isPartner || isCompany} testid="field-azienda" />
        )}
        <Field label={t("cf_email")} type="email" value={form.email} onChange={update("email")} required testid="field-email" />
        <Field label={t("cf_cell")} type="tel" value={form.telefono} onChange={update("telefono")} required testid="field-telefono" />
      </div>

      <div className="mt-5">
        <AddressAutocomplete
          label={(isPrivato || isCompany) ? t("cf_indirizzo_req") : t("cf_indirizzo")}
          value={form.indirizzo}
          onChange={(v) => setForm((f) => ({ ...f, indirizzo: v }))}
          required={isPrivato || isCompany}
        />
      </div>

      {isCompany && (
        <div className="mt-8 border-t border-black/10 pt-8" data-testid="company-intent">
          <Pills
            label={t("cf_company_label")}
            options={[t("cf_company_installer"), t("cf_company_studio"), t("cf_company_info"), t("cf_company_turnkey")]}
            value={form.companyType}
            onChange={(v) => setForm((f) => ({ ...f, companyType: v }))}
            name="companyType"
          />
        </div>
      )}

      {isPartner && (
        <div className="mt-8 border-t border-black/10 pt-8 space-y-7" data-testid="partner-questionnaire">
          <span className="block text-black/60 text-xs uppercase tracking-[0.14em]" style={{ fontFamily: "Space Grotesk" }}>{t("cf_profilo")}</span>

          <YesNo label={t("cf_q_installs")} value={partner.installsDirectly} onChange={(v) => setP("installsDirectly", v)} name="installsDirectly" />

          <CheckGroup label={t("cf_q_types")} options={TYPES} selected={partner.types} onToggle={(v) => toggleArr("types", v)} name="types" />

          <CheckGroup label={t("cf_q_activities")} options={ACTIVITIES} selected={partner.activities} onToggle={(v) => toggleArr("activities", v)} name="activities" />

          <YesNo label={t("cf_q_internal")} value={partner.internalTeams} onChange={(v) => setP("internalTeams", v)} name="internalTeams" />

          <Pills label={t("cf_q_numteams")} options={TEAMS} value={partner.numTeams} onChange={(v) => setP("numTeams", v)} name="numTeams" />

          <Pills label={t("cf_q_power")} options={POWERS} value={partner.annualPower} onChange={(v) => setP("annualPower", v)} name="annualPower" />

          <YesNo label={t("cf_q_cert")} value={partner.electricalCert} onChange={(v) => setP("electricalCert", v)} name="electricalCert" />

          <YesNo label={t("cf_q_turnkey")} value={partner.availableTurnkey} onChange={(v) => setP("availableTurnkey", v)} name="availableTurnkey" />

          <YesNo label={t("cf_q_maint")} value={partner.availableMaintenance} onChange={(v) => setP("availableMaintenance", v)} name="availableMaintenance" />

          <div>
            <label className="block text-black/50 text-xs uppercase tracking-[0.14em] mb-2" style={{ fontFamily: "Space Grotesk" }}>{t("cf_area")}</label>
            <input
              type="text"
              value={partner.operatingArea}
              onChange={(e) => setP("operatingArea", e.target.value)}
              placeholder={t("cf_area_ph")}
              data-testid="field-operatingArea"
              className="w-full border border-black/20 px-4 py-3 text-black bg-white placeholder-black/30 focus:outline-none focus:border-black transition-colors"
              style={{ fontFamily: "Inter" }}
            />
          </div>
        </div>
      )}

      <div className="mt-5">
        <label className="block text-black/50 text-xs uppercase tracking-[0.14em] mb-2" style={{ fontFamily: "Space Grotesk" }}>{t("cf_msg")}</label>
        <textarea
          rows={5}
          value={form.messaggio}
          onChange={update("messaggio")}
          data-testid="field-messaggio"
          placeholder={isPartner ? t("cf_msg_ph_partner") : t("cf_msg_ph")}
          className="w-full border border-black/20 px-4 py-3 text-black bg-white placeholder-black/30 focus:outline-none focus:border-black transition-colors resize-none"
          style={{ fontFamily: "Inter" }}
        />
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <button
          type="submit"
          disabled={loading}
          data-testid="contact-submit"
          className="group inline-flex items-center gap-3 px-8 py-4 text-black text-sm uppercase tracking-[0.14em] font-semibold transition-transform hover:scale-[1.03] disabled:opacity-60"
          style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}
        >
          {loading ? t("cf_submitting") : sent ? (<><Check size={18} /> {t("cf_sent")}</>) : (<>{isPartner ? t("cf_submit_partner") : t("cf_submit")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>)}
        </button>
        <p className="text-black/60 text-xs max-w-xs" style={{ fontFamily: "Inter" }}>
          {t("cf_privacy")}
        </p>
      </div>

      {sent && (
        <p className="mt-5 text-sm text-black/70 bg-[#F9E212]/20 border border-[#F9E212] px-4 py-3" data-testid="contact-success" style={{ fontFamily: "Inter" }}>
          {t("cf_success")}
        </p>
      )}
      {error && (
        <p className="mt-5 text-sm text-red-700 bg-red-50 border border-red-300 px-4 py-3" data-testid="contact-error" style={{ fontFamily: "Inter" }}>
          {error}
        </p>
      )}
    </form>
  );
}

function AddressAutocomplete({ label, value, onChange, required }) {
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const token = useRef(Math.random().toString(36).slice(2));

  useEffect(() => { setQ(value || ""); }, [value]);

  const handleInput = (val) => {
    setQ(val);
    onChange(val);
    if (timer.current) clearTimeout(timer.current);
    if (val.trim().length < 3) { setItems([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/geo/autocomplete?q=${encodeURIComponent(val)}&session_token=${token.current}`);
        const data = await res.json();
        setItems(data.items || []);
        setOpen((data.items || []).length > 0);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 350);
  };

  const pick = (it) => {
    onChange(it.label);
    setQ(it.label);
    setItems([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      <label className="block text-black/50 text-xs uppercase tracking-[0.14em] mb-2" style={{ fontFamily: "Space Grotesk" }}>{label}</label>
      <div className="relative">
        <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/60" />
        <input
          type="text"
          value={q}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => items.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          required={required}
          autoComplete="off"
          data-testid="field-indirizzo"
          placeholder={t("cf_indirizzo_ph")}
          className="w-full border border-black/20 pl-10 pr-4 py-3 text-black bg-white placeholder-black/30 focus:outline-none focus:border-black transition-colors"
          style={{ fontFamily: "Inter" }}
        />
      </div>
      {open && items.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border border-black/20 shadow-lg max-h-64 overflow-auto" data-testid="address-suggestions">
          {items.map((it, i) => (
            <li key={it.place_id || i}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                data-testid={`address-suggestion-${i}`}
                className="w-full text-left px-4 py-2.5 text-sm text-black/80 hover:bg-black/5 flex items-start gap-2 transition-colors"
                style={{ fontFamily: "Inter" }}
              >
                <MapPin size={14} className="mt-0.5 shrink-0 text-[#c9a200]" /> {it.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {loading && <span className="absolute right-3 top-9 text-xs text-black/60" style={{ fontFamily: "Space Grotesk" }}>…</span>}
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="block text-black/50 text-xs uppercase tracking-[0.14em] mb-2" style={{ fontFamily: "Space Grotesk" }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full border border-black/20 px-4 py-3 text-black bg-white placeholder-black/30 focus:outline-none focus:border-black transition-colors"
        style={{ fontFamily: "Inter" }}
      />
    </div>
  );
}

function YesNo({ label, value, onChange, name }) {
  return (
    <div>
      <label className="block text-black/70 text-sm mb-2" style={{ fontFamily: "Inter" }}>{label}</label>
      <div className="flex gap-3">
        {["Sì", "No"].map((o) => (
          <button
            key={o}
            type="button"
            data-testid={`${name}-${o === "Sì" ? "si" : "no"}`}
            onClick={() => onChange(o)}
            className={`px-6 py-2 text-sm border transition-colors ${value === o ? "bg-black text-white border-black" : "border-black/20 text-black/70 hover:border-black"}`}
            style={{ fontFamily: "Space Grotesk" }}
          >
            {o === "Sì" ? t("cf_yes") : t("cf_no")}
          </button>
        ))}
      </div>
    </div>
  );
}

function Pills({ label, options, value, onChange, name }) {
  return (
    <div>
      <label className="block text-black/70 text-sm mb-2" style={{ fontFamily: "Inter" }}>{label}</label>
      <div className="flex flex-wrap gap-2.5">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            data-testid={`${name}-${o}`}
            onClick={() => onChange(o)}
            className={`px-4 py-2 text-sm border transition-colors ${value === o ? "bg-black text-white border-black" : "border-black/20 text-black/70 hover:border-black"}`}
            style={{ fontFamily: "Space Grotesk" }}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function CheckGroup({ label, options, selected, onToggle, name }) {
  return (
    <div>
      <label className="block text-black/70 text-sm mb-2" style={{ fontFamily: "Inter" }}>{label}</label>
      <div className="flex flex-wrap gap-2.5">
        {options.map((o) => {
          const active = selected.includes(o);
          return (
            <button
              key={o}
              type="button"
              data-testid={`${name}-${o.toLowerCase().replace(/\s+|&/g, "-")}`}
              onClick={() => onToggle(o)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm border transition-colors ${active ? "bg-black text-white border-black" : "border-black/20 text-black/70 hover:border-black"}`}
              style={{ fontFamily: "Space Grotesk" }}
            >
              {active && <Check size={14} />} {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
